// Reminder-days CSV parser + validator (W3.1 §4.2 + BR-03).
//
// reminder_days is `list[int]` subset of {0,1,3,7,14,30}, length 0-6, ascending.
// Backend also accepts the literal CSV form (e.g. "0,3,7") in URL params
// during testing — parse + validate here so the UI form round-trips losslessly.

export const REMINDER_DAYS_ALLOWED: readonly number[] = Object.freeze([0, 1, 3, 7, 14, 30]);

export const REMINDER_DAYS_LABEL_ZH: Readonly<Record<number, string>> = Object.freeze({
  0: "當天",
  1: "1 天",
  3: "3 天",
  7: "7 天",
  14: "14 天",
  30: "30 天",
});

/**
 * Server-side validation rules per `architecture.md §8.5` + W3.1 BR-03.
 * Returns a structured result so callers can surface field-level errors.
 */
export interface ReminderDaysValidation {
  ok: boolean;
  /** Subset of the input; may be empty (legal) when the input fails subset check. */
  normalized: number[];
  /** First failure reason; absent when ok. */
  reason?:
    | "TOO_SHORT" // empty list (allowed actually, but we surface for clarity)
    | "TOO_LONG"
    | "INVALID_VALUE"
    | "NOT_ASCENDING"
    | "DUPLICATE";
  message?: string;
}

/** Single-pass validator: subset + length 0-6 + ascending + dedup. */
export function validateReminderDays(values: readonly number[]): ReminderDaysValidation {
  if (!Array.isArray(values)) {
    return { ok: false, normalized: [], reason: "INVALID_VALUE", message: "請選擇提醒天數" };
  }
  if (values.length > 6) {
    return {
      ok: false,
      normalized: [],
      reason: "TOO_LONG",
      message: "提醒天數最多 6 個",
    };
  }
  const seen = new Set<number>();
  const dedup: number[] = [];
  for (const v of values) {
    if (typeof v !== "number" || !Number.isFinite(v) || !Number.isInteger(v)) {
      return {
        ok: false,
        normalized: [],
        reason: "INVALID_VALUE",
        message: "提醒天數必須為整數",
      };
    }
    if (!REMINDER_DAYS_ALLOWED.includes(v)) {
      return {
        ok: false,
        normalized: [],
        reason: "INVALID_VALUE",
        message: `提醒天數必須為 {0,1,3,7,14,30} 的子集`,
      };
    }
    if (seen.has(v)) {
      return {
        ok: false,
        normalized: [],
        reason: "DUPLICATE",
        message: "提醒天數不可重複",
      };
    }
    seen.add(v);
    dedup.push(v);
  }
  for (let i = 1; i < dedup.length; i += 1) {
    if (dedup[i]! <= dedup[i - 1]!) {
      return {
        ok: false,
        normalized: [],
        reason: "NOT_ASCENDING",
        message: "提醒天數必須升冪排序",
      };
    }
  }
  return { ok: true, normalized: dedup };
}

/**
 * Parse a CSV string ("0,3,7") into the validated array form.
 * Used by query-string exports (e.g. debug pages).
 */
export function parseReminderDaysCsv(csv: string | null | undefined): ReminderDaysValidation {
  if (!csv) return { ok: true, normalized: [] };
  const parts = csv
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  const nums: number[] = [];
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isInteger(n)) {
      return {
        ok: false,
        normalized: [],
        reason: "INVALID_VALUE",
        message: `提醒天數不可解析: "${p}"`,
      };
    }
    nums.push(n);
  }
  return validateReminderDays(nums);
}

/**
 * Sort + dedup an arbitrary user-picked subset. Useful when the user toggles
 * chips in non-ascending order — we always emit ascending to the backend.
 */
export function normalizeReminderDays(values: readonly number[]): number[] {
  const v = validateReminderDays(values);
  return v.ok ? v.normalized : [];
}